import { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Network, TreePine } from "lucide-react";
import { FlowCommissionItem } from "../../../../Redux/referral/types";
import {
  PRODUCT_IMAGE_FALLBACK,
  earningsStatusClass,
  formatEarningsCurrency,
  mapFlowItemToRecord,
  statusLabel,
} from "../earningsAdapters";

interface Props {
  data: FlowCommissionItem[];
}

const ReferralCommTable = ({ data }: Props) => {
  const navigate = useNavigate();
  const records = (data || []).map(mapFlowItemToRecord);

  const go = (path: string, event?: MouseEvent) => {
    event?.stopPropagation();
    navigate(path);
  };

  return (
    <div className="ri-table-wrap ri-main-table-wrap">
      <table className="ri-table ri-main-table">
        <thead>
          <tr>
            <th>S.NO</th>
            <th>ORDER DATE</th>
            <th>ORDER NO</th>
            <th>PRODUCT DETAILS</th>
            <th>REFERRAL NETWORK STATUS</th>
            <th>REFERRAL INCENTIVES</th>
            <th>TDS</th>
            <th>NET AMOUNT</th>
            <th>BADGES</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr
              key={record.id}
              style={{ cursor: "pointer" }}
            >
              <td className="ri-center">
                <strong className="ri-serial">{record.serialNo}</strong>
              </td>

              <td className="ri-date-cell">
                <strong>{record.orderDateFormatted || record.orderDate}</strong>
                <small>{record.orderTime}</small>
              </td>

              <td className="ri-center">
                <strong className="ri-order-no">{record.orderNo}</strong>
              </td>

              <td>
                <div className="ri-product-cell">
                  <img src={PRODUCT_IMAGE_FALLBACK} alt="" />
                  <div>
                    <p>
                      <b>Product Name:</b> {record.productName}
                    </p>
                    <p>
                      <b>Order Type:</b> {record.orderType}
                    </p>
                    <p>
                      <b>SF Network No:</b> {record.sfNetworkNo}
                    </p>
                    {record.uplinerName && (
                      <p>
                        <b>Upliner Name:</b> {record.uplinerName}
                      </p>
                    )}
                    {record.uplinerId && (
                      <p>
                        <b>Upliner ID:</b> {record.uplinerId}
                      </p>
                    )}
                  </div>
                </div>
              </td>

              <td>
                <div className="ri-network-cell">
                  <div className="ri-data-line">
                    <span>Contribution Requirement</span>
                    <strong>{record.contributionRequirement}</strong>
                  </div>
                  <div className="ri-data-line">
                    <span>Active Contributors</span>
                    <strong>{record.activeContributors}</strong>
                  </div>
                  <div className="ri-network-actions">
                    <button
                      type="button"
                      onClick={(e) =>
                        go(
                          `/ReferralCommissoinTeam?orderNo=${record.orderNo}`,
                          e,
                        )
                      }
                    >
                      <Network size={15} />
                      Network
                    </button>
                    <button
                      type="button"
                      onClick={(e) =>
                        go(
                          `/Tree?orderNo=${record.orderNo}&type=referral`,
                          e,
                        )
                      }
                    >
                      <TreePine size={15} />
                      Tree
                    </button>
                  </div>
                  <div className="ri-status-row">
                    <span>Status</span>
                    <span className={earningsStatusClass(record.flowStatus)}>
                      {statusLabel(record.flowStatus)}
                    </span>
                  </div>
                </div>
              </td>

              <td className="ri-center">
                <strong className="ri-money">
                  {formatEarningsCurrency(record.grossAmount || record.referralIncentives)}
                </strong>
              </td>

              <td className="ri-center">
                <strong className="ri-money">
                  {formatEarningsCurrency(record.tdsAmount ?? 0)}
                  {record.tdsIsEstimated ? " (est)" : ""}
                </strong>
              </td>

              <td className="ri-center">
                <strong className="ri-money">
                  {formatEarningsCurrency(record.netAmount ?? record.referralIncentives)}
                </strong>
              </td>

              <td className="ri-center">
                <span
                  className={`ri-main-badge ri-main-badge-${
                    record.achieved ? "combination" : "regular"
                  }`}
                >
                  {record.achieved ? "Achieved" : "Not Achieved"}
                </span>
              </td>
            </tr>
          ))}
          {records.length === 0 && (
            <tr>
              <td colSpan={10} className="ri-center">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ReferralCommTable;
